import { Download, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { InstallInstructions } from '@/components/pwa/InstallInstructions';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';

export default function Install() {
  const { deferredPrompt, platformInfo, handleInstall, trackManualInstall } = useInstallPrompt();

  const handleInstallClick = async () => {
    const outcome = await handleInstall();
    if (outcome === 'accepted') {
      await trackManualInstall();
    }
  };

  return (
    <div className="container max-w-4xl py-8 px-4">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-4">Install BuildCRM</h1>
        <p className="text-lg text-muted-foreground">
          Get the full app experience with offline access and faster performance
        </p>
      </div>

      {platformInfo.isStandalone ? (
        <Card className="mb-8 border-green-500 bg-green-50 dark:bg-green-950">
          <CardContent className="flex items-center gap-3 py-6">
            <CheckCircle className="h-8 w-8 text-green-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-green-900 dark:text-green-100 text-lg">Already installed!</p>
              <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                You're using BuildCRM as an installed app. You can access it from your home screen anytime.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {deferredPrompt && (
            <Card className="mb-8 bg-primary/5 border-primary/20">
              <CardContent className="py-6">
                <div className="text-center space-y-4">
                  <p className="text-muted-foreground">
                    Your browser supports one-click installation
                  </p>
                  <Button onClick={handleInstallClick} size="lg" className="w-full sm:w-auto">
                    <Download className="mr-2 h-5 w-5" />
                    Install BuildCRM Now
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="mb-8">
            <InstallInstructions platform={platformInfo.platform} />
          </div>
        </>
      )}

      <Card>
        <CardContent className="py-6">
          <h2 className="text-xl font-semibold mb-4">Why Install BuildCRM?</h2>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <strong className="block">Faster Access</strong>
                <span className="text-sm text-muted-foreground">Launch directly from your home screen like a native app</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <strong className="block">Works Offline</strong>
                <span className="text-sm text-muted-foreground">View and manage your data even without internet</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <strong className="block">Push Notifications</strong>
                <span className="text-sm text-muted-foreground">Stay updated with real-time alerts for important events</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <strong className="block">Better Performance</strong>
                <span className="text-sm text-muted-foreground">Optimized for your device with instant loading</span>
              </div>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
